import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/** A user whose friend list (mutual follows) the admin wants to inspect. */
export type FriendsUser = { id: string; username: string | null };

type FriendRow = {
  user_id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  friends_since: string | null;
};

function rpcMissing(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('admin_list_user_friends') || m.includes('42883') || m.includes('could not find the function');
}

/** Mutual-follows list for one user. Depends on admin_list_user_friends() —
 * supabase/sql/admin_list_user_friends.sql (not yet applied). */
export function UserFriendsModal({ user, onClose }: { user: FriendsUser | null; onClose: () => void }) {
  const [friends, setFriends] = useState<FriendRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase.rpc('admin_list_user_friends', { p_user_id: userId });
      if (e) {
        if (rpcMissing(e.message)) {
          throw new Error(
            'Friends list is not enabled yet — run supabase/sql/admin_list_user_friends.sql against production.',
          );
        }
        throw e;
      }
      setFriends((data ?? []) as FriendRow[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load friends');
      setFriends([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) void load(user.id);
  }, [user?.id, load]);

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="w-full max-w-lg rounded-xl border border-border bg-surface" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="admin-label-primary text-base truncate">
              @{user.username ?? user.id.slice(0, 8)}'s friends
            </h2>
            <div className="text-xs text-mutedDim mt-0.5">
              Mutual follows{!loading ? ` · ${friends.length.toLocaleString()}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted">Loading friends…</p>
          ) : error ? (
            <div className="rounded-lg border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          ) : friends.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">No mutual follows yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {friends.map((f) => (
                <li
                  key={f.user_id}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-white/[0.03]"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-border text-xs text-muted">
                    {f.avatar_url ? (
                      <img src={f.avatar_url} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" />
                    ) : (
                      (f.username ?? '?').slice(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-textPrimary">@{f.username ?? f.user_id.slice(0, 8)}</div>
                    {f.full_name?.trim() ? (
                      <div className="truncate text-xs text-mutedDim">{f.full_name}</div>
                    ) : null}
                  </div>
                  {f.friends_since ? (
                    <span className="shrink-0 text-[11px] text-mutedDim tabular-nums">
                      since {timeAgo(f.friends_since)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
